/**
 * A QUOTE THAT IS A YES TO A FREE VISIT, NOT A PRICE.
 *
 * Anthony, 2026-09-29, about 24/7 Swift Garage Door Services: "modify
 * quotes a bit, we just want a quote to be the customer saying yes to a
 * free visit for an estimate. This means quotes can auto send because they
 * are not estimates, just confirmation a person wants an appointment. Next
 * step is schedule still."
 *
 * ─── WHY A DOOR IS NOT QUOTED OFF A TEXT ────────────────────────────────
 *
 * A garage door is priced standing in front of it. Which spring, what
 * size, whether the opener is worth saving, whether the track is bent: a
 * customer cannot tell you any of that, and the estimator could only ever
 * guess at it. Worse, the drafter never drafted a single Swift quote. It
 * refuses anything without a car's year, make and model, and nobody who
 * rings a door company says one. So the Sales Desk sat empty and every
 * Swift lead waited for a person.
 *
 * So for a trade whose vocabulary says `quoteIsVisit` (garage_door, and
 * only garage_door), the quote is ONE LINE, "Free on-site estimate visit",
 * at nothing. No labor, no parts, no estimator call. The technician prices
 * the real work at the door.
 *
 * ─── WHY THIS ONE MAY SEND ITSELF ──────────────────────────────────────
 *
 * The house rule is that a bot never shows a customer a total, and the
 * two exceptions so far (a pre-purchase inspection, a flat-price first
 * caller) are both about a price nobody can get wrong. This one has no
 * price at all. The worst a machine can do with it is offer a free visit
 * to somebody who asked about their door, which is what the shop would
 * have said anyway.
 *
 * IT IS STILL AN ALLOWLIST, the same shape as maySendUnread in flatsend.ts.
 * `isVisitOnly` says yes only when EVERY line on the job is the visit line
 * at no charge. The moment a person adds a $300 spring to it, it is a real
 * priced quote again and goes back to being a person's to send. A gate
 * that looked for "is there a visit line" instead would text the spring.
 *
 * ─── AND THE YES IS A REAL YES ─────────────────────────────────────────
 *
 * The customer's YES is recorded as an authorization, the same row a
 * signed quote writes, for $0. That is what moves the job to Approved on
 * every screen ("They said yes. Book it.") and is why the next step is the
 * one it always was: give it a day. The amount checks that refuse a $0
 * approval everywhere else ("Enter the figure they agreed to") step aside
 * only for a job this module recognises, and for nothing else.
 */

import { words, type Vocabulary } from "./trade.ts";

/** What the line says, on the desk, the invoice and the customer's page. */
export const VISIT_LINE = "Free on-site estimate visit";

/**
 * What goes in line_items.flat (0129), set where the line is CHOSEN, so
 * the gate below never has to judge the description alone. The desk's
 * own line editor never writes it; only the drafter does.
 */
export const VISIT_FLAT = "free_estimate_visit";

/** Whether this trade's quote is a free visit rather than a price. */
export const quotesAVisit = (trade: string | null | undefined): boolean =>
  words(trade).quoteIsVisit;

/** The one line a visit quote carries. */
export const visitLine = () => ({
  kind: "labour" as const,
  description: VISIT_LINE,
  quantity: 1,
  unit_price: 0,
  cost: null,
  part_number: null,
  flat: VISIT_FLAT,
});

type AnyLine = {
  description?: string | null;
  flat?: string | null;
  unit_price?: number | string | null;
  total?: number | string | null;
  quantity?: number | string | null;
};

const money = (l: AnyLine): number =>
  l.total !== undefined && l.total !== null
    ? Number(l.total) || 0
    : (Number(l.quantity ?? 1) || 0) * (Number(l.unit_price ?? 0) || 0);

/**
 * IS THIS JOB A FREE VISIT AND NOTHING ELSE.
 *
 * At least one line, and every line is the visit line with no money on it.
 * The description is accepted as well as the flat marker because the
 * browser's LineItem shape does not carry `flat`; the zero price is what
 * makes either one safe, since a line nobody is charged for cannot put a
 * figure in front of anybody.
 */
export function isVisitOnly(lines: AnyLine[] | null | undefined): boolean {
  const all = lines ?? [];
  if (!all.length) return false;
  return all.every((l) =>
    (l.flat === VISIT_FLAT || String(l.description ?? "").trim() === VISIT_LINE)
    && Math.abs(money(l)) < 0.005);
}

/**
 * WHAT A SCREEN PRINTS WHERE THE MONEY WOULD GO. "$0.00" on a quote reads
 * as a mistake, or as a price somebody forgot to fill in. "Free estimate"
 * reads as the offer it is. Null means print the money as usual.
 */
export function visitAmountLabel(
  trade: string | null | undefined, total: number, lines?: AnyLine[] | null,
): string | null {
  if (!quotesAVisit(trade)) return null;
  if (Math.abs(Number(total) || 0) >= 0.005) return null;
  if (lines && !isVisitOnly(lines)) return null;
  return "Free estimate";
}

const greet = (first: string, speaker: string): string =>
  first && first.toLowerCase() !== speaker.toLowerCase() ? `Hi ${first}` : "Hi";

/**
 * Who is talking. A person signs as themselves and says where from; a
 * shop that named nobody signs as the shop, once, not "Swift here from
 * Swift".
 */
const signed = (speaker: string, shopName: string): string =>
  !shopName || speaker.toLowerCase() === shopName.toLowerCase()
    ? `${speaker} here`
    : `${speaker} here from ${shopName}`;

/**
 * THE OFFER ITSELF, the text that goes out with nobody reading it.
 *
 * Anthony's voice: short, one question, a real next step. No price,
 * because there is none, and no link, because the answer is one word and
 * a link makes a yes feel like paperwork. "Reply YES" is the one
 * instruction, and it is the word the reply pass reads.
 */
export function visitOfferText(
  first: string, speaker: string, shopName: string, optOut: string,
  trade: string | null | undefined = "garage_door",
): string {
  const w: Vocabulary = words(trade);
  return `${greet(first, speaker)}, ${signed(speaker, shopName)}. `
    + `We can come out and look at ${w.theirs} for a free estimate. `
    + `Want us to set up a time? Reply YES. ${optOut}`;
}

/** What the customer is told after their YES. A person books the time. */
export function visitYesAck(first: string): string {
  return `${first ? `Thanks ${first}` : "Thanks"}, you're on our list. `
    + `We'll text you shortly to set up a time.`;
}

/**
 * THE CHASE, when the offer went and nothing came back.
 *
 * quote-chase.ts sends four different messages because the same sentence
 * three mornings running is what tells somebody it is a robot. A visit
 * gets its own four, because "did you get the quote? $0" is the wrong
 * question: there is no quote to get, only a yes to ask for again.
 */
export function visitChaseText(
  n: number, first: string, trade: string | null | undefined = "garage_door",
): string {
  const w = words(trade);
  const hi = first ? `Hi ${first}` : "Hi";
  if (n <= 1) {
    return `${hi}, still want us to come out and look at ${w.theirs}? `
      + `The estimate is free. Reply YES and we'll set up a time.`;
  }
  if (n === 2) {
    return `${hi}, happy to swing by and take a look at the ${w.thing}, no charge `
      + `to look. Want a time? Reply YES.`;
  }
  if (n === 3) return `${hi}, do you still need the ${w.thing} looked at? Reply YES.`;
  return `${hi}, last one from me. The free estimate stands if you want it, `
    + `just reply YES.`;
}

/**
 * Our own chase messages, recognised in the thread, because the thread is
 * the only record of what the customer actually received. Matches every
 * wording above and nothing a person would plausibly type.
 */
export const VISIT_CHASE_RE =
  /still want us to come out and look|happy to swing by and take a look|do you still need the \w+ looked at\? reply yes|the free estimate stands/i;

/**
 * DID THEY SAY YES.
 *
 * The whole reply, not a word inside it. "Yes but not till Friday" is a
 * yes with a condition a person has to read, and "no, yes I mean" is a
 * sentence nobody should let a regex decide. Anything that is not plainly
 * a yes goes to a person, which is the cheap direction to be wrong in:
 * the cost is a slower booking, not a visit nobody asked for.
 */
export function saidYesToVisit(text: string | null | undefined): boolean {
  const t = String(text ?? "").toLowerCase()
    .replace(/['’]/g, "").replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
  if (!t) return false;
  return /^(yes|y|yep|yeah|yea|yup|ya|yes please|yes pls|yes sir|yes maam|sure|ok|okay|k|please|please do|sounds good|do it|go ahead|absolutely|definitely|of course|lets do it|yes lets do it|that works|works for me)$/
    .test(t);
}

/**
 * DOES THE YES ON FILE COVER WHAT IS ON THE JOB NOW.
 *
 * A yes to a free visit is a yes at $0, and it must not quietly become a
 * yes to the $400 spring the technician writes up at the door. Before
 * this, a $0 authorization could not exist at all (every door that writes
 * one refused a zero), so the only $0 yes is a visit yes, and it covers a
 * job with nothing priced on it and nothing else. Once there is a price,
 * only a yes with money on it counts, and the job card asks for one.
 */
export function yesCovers(
  auths: Array<{ amount: number | string | null; declined?: boolean | null }> | null | undefined,
  hasPricedLine: boolean,
): boolean {
  const live = (auths ?? []).filter((a) => !a.declined);
  if (!live.length) return false;
  if (!hasPricedLine) return true;
  return live.some((a) => Number(a.amount) > 0);
}
